import React, { useEffect, useRef, useState } from 'react'
import { AppSvg, FormattedHTMLMessage, LinkButton, icons } from '@web/walter-ui-kit'
import { rpPosthogEvents } from '@walter/shared/constants/posthog'
import { logPosthogEvent } from '@walter/shared/utils/posthog'

import styles from './style.module.css'
import { lang } from './lang'
import { ActionStatus } from '~components/ResendInput'

interface Props {
    initialInputState?: ActionStatus
    onResendCode: () => void
}

const RESEND_SECONDS = 60

function ResendCode({ onResendCode, initialInputState }: Props) {
    const [status, setStatus] = useState<ActionStatus>(initialInputState || 'none')
    const [seconds, setSeconds] = useState(status === 'sent' ? RESEND_SECONDS : 0)
    const requestFrame = useRef(0)
    const firstCall = useRef(0)
    const deltaSeconds = useRef(0)

    const isCountdownActive = seconds > 0

    const onResend = async () => {
        setStatus('pending')
        await onResendCode()
        setSeconds(RESEND_SECONDS)
        setStatus('sent')
    }

    useEffect(() => {
        if (initialInputState === 'pending') {
            onResend()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialInputState])

    useEffect(() => {
        if (!isCountdownActive) {
            return
        }

        firstCall.current = Date.now()

        const render = () => {
            const tempDeltaSeconds = Math.abs(Math.ceil((firstCall.current - Date.now()) / 1000))

            if (deltaSeconds.current === tempDeltaSeconds) {
                requestFrame.current = requestAnimationFrame(render)
                return
            }

            deltaSeconds.current = tempDeltaSeconds
            setSeconds(RESEND_SECONDS - deltaSeconds.current)
            requestFrame.current = requestAnimationFrame(render)
        }

        requestFrame.current = requestAnimationFrame(render)
        return () => cancelAnimationFrame(requestFrame.current)
    }, [isCountdownActive])

    let button

    if (status === 'pending') {
        button = <FormattedHTMLMessage {...lang.sending} />
    } else if (isCountdownActive) {
        button = <FormattedHTMLMessage {...lang.resendInCode} values={{ seconds }} />
    } else {
        button = <FormattedHTMLMessage {...lang.resend} />
    }

    return (
        <LinkButton
            dataPointer="resend_button"
            isDisabled={isCountdownActive || status === 'pending'}
            size="xs"
            onClick={() => {
                onResend()
                logPosthogEvent(rpPosthogEvents.sendCryptoResendEmail)
            }}
            htmlButtonType="button"
        >
            <div data-pointer="icon" className={styles.icon}>
                <AppSvg svgName={icons.ic18RoundArrow} size={12} />
            </div>

            {button}
        </LinkButton>
    )
}

export default ResendCode
